import React, { useEffect, useState } from 'react';
import NumberFormat from 'react-number-format';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import Link from '@material-ui/core/Link';
import { navigate } from '@gatsbyjs/reach-router';
import { useSelector } from 'react-redux';

import { ReactComponent as CheckedIcon } from '../../assets/checkbox-checked.svg';
import { Button } from '@components';
import { PayModal } from '@views/Payments/PayModal';
import PaymentPage from '@views/Payments/PaymentPage';
import { ChoosePaymentOption } from '@views/Payments/ChoosePaymentOption';
import { BankTransferPayment } from '@views/Payments/BankTransferPayment';
import { CardPaymentSuccess } from '@views/Payments/CardPaymentSuccess';
import CreditCardPayment from '@views/Payments/CreditCardPayment';
import {
  achDefaultValue,
  OrderDetailsProvider,
  useOrderDetails,
} from '@hooks/useOrderDetails';
import { PaymentCompleteInfo } from '@views/Payments/PaymentCompleteInfo';
import { getLocalizedContent as l } from '@helpers/languages';
import content from './Payments.json';
import { getOrderDocument } from '@api/api';
import { retrieveAccessToken } from '@utils/auth';
import {
  amountToCurrencyString,
  formatAmountToCurrencyString,
  formatPriceWithCents,
} from '@utils/currency';
import { isGreaterWithTolerance } from '@utils';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'SchnyderSLight',
  },
  errorBox: {
    border: '2px solid #FF0000',
  },
  errorText: {
    color: '#FF0000',
  },
  '.hide': {
    display: 'none',
  },
  paymentOption: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '24px',
  },
  creditCardContainer: {
    marginTop: '15px',
    display: 'flex',
    width: '100%',
    padding: '15px 0px 15px 30px',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: '8px',
  },
  paymentOptionDescription: {
    marginLeft: '30px',
    color: 'var(--Grayscale-Shadow, #555354)',
    fontSize: '12px',
    lineHeight: '18px',
  },
  paymentAmountLabel: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '16px',
    letterSpacing: '2.16px',
    textTransform: 'uppercase',
    fontStretch: 'condensed',
  },
  textField: {
    '& .MuiOutlinedInput-input': {
      display: 'flex',
      alignItems: 'flex-start',
      alignSelf: 'stretch',
      padding: '14px 24px',
    },
    '& .MuiOutlinedInput-root': {
      '&.Mui-focused fieldset': {
        border: '1px solid var(--Grayscale-Fossil, #BEBBBB)',
      },
      '&.Mui-error fieldset': {
        border: '1px solid #FF0000',
      },
    },
    '& .MuiFormHelperText-contained': {
      marginLeft: '0px',
    },
  },
  paymentTotal: {
    color: 'var(--Nero, #1B1B1B)',
    fontSize: '14px',
    fontWeight: 300,
    lineHeight: '24px',
    letterSpacing: '-0.07px',
  },
  paySecurelyLabel: {
    color: 'var(--Nero, #1B1B1B)',
    fontSize: '22px',
    fontStyle: 'normal',
    fontWeight: '400',
    lineHeight: '130%',
    letterSpacing: '-0.44px',
  },
  paymentOptionLabel: {
    color: '#1B1B1B',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: '300',
    lineHeight: '24px',
    letterSpacing: '-0.14px',
  },
  paymentTypeLabel: {
    color: 'var(--Grayscale-Nero, #1B1B1B)',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: '300',
    lineHeight: '20px',
    letterSpacing: '-0.08px',
  },
  disabledButton: {
    background: 'var(--Grayscale-Fossil, #BEBBBB) !important',
    border: '0px solid rgb(64, 66, 63) !important',
    '@media screen and (max-width: 700px)': {
      width: '75% !important',
      marginLeft: '10px',
    },
  },
  activeButton: {
    background: 'var(--Grayscale-Nero, #1B1B1B)',
    '@media screen and (max-width: 700px)': {
      width: '75% !important',
      marginLeft: '10px',
    },
  },
}));

const PAY_VIA_BANK_TRANSFER = 'PAY_VIA_BANK_TRANSFER';
const PAY_VIA_CREDIT_CARD = 'PAY_VIA_CREDIT_CARD';
const CHOOSE_PAYMENT_OPTION = 'CHOOSE_PAYMENT_OPTION';
const PAYMENT_COMPLETE_INFO = 'PAYMENT_COMPLETE_INFO';
const PAYMENT_SUCCESS = 'PAYMENT_SUCCESS';

const isReceiptAvailable = async orderId => {
  try {
    const token = retrieveAccessToken();
    const orderDocumentResponse = await getOrderDocument(token, `${orderId}`);
    if (orderDocumentResponse) {
      return true;
    }
  } catch (err) {
    // eslint-disable-next-line no-console
    console.log({ err });
    return false;
  }
  return false;
};

const Payments = ({ location, orderId, basepath, isOauth = false }) => {
  const [isPlaidDialogOpen, setIsPlaidDialogOpen] = useState(false);
  const [initialBottomContent, setInitialBottomContent] = useState(
    CHOOSE_PAYMENT_OPTION
  );
  if (isOauth) {
    setInitialBottomContent(PAY_VIA_BANK_TRANSFER);
  }
  const [display, setDisplay] = useState(initialBottomContent);
  const {
    appState: { prefixLanguage },
    userProfile,
  } = useSelector(state => state);
  const user = userProfile?.user;
  const language = prefixLanguage;
  const customer = {
    firstName: user?.firstName || 'Jenifer',
    lastName: user?.lastName || 'Smith',
    phoneNumber: user?.phone || '123-456-6789',
    email: user?.email || 'jsmith@gmail.com',
    language: language || 'en',
    country: user?.primaryBilling?.country || 'US',
    primaryBilling: user?.primaryBilling,
    primaryShipping: user?.primaryShipping,
    locale: user?.primaryBilling?.country || 'US',
  };

  const [paymentType, setPaymentType] = useState(
    language === 'en-ca' || language === 'fr-ca' ? 'ach' : ''
  );
  const [selectedAmount, setSelectedAmount] = useState({
    amount: 0,
    currency: 'USD',
  });
  const [paymentAmountError, setPaymentAmountError] = useState('');
  const [successData, setSuccessData] = useState(null);
  const [total, setTotal] = useState({});
  const [balance, setBalance] = useState({});
  const [payments, setPayments] = useState([]);
  const [sfOrder, setSfOrder] = useState({});
  const [hasPendingPayments, setHasPendingPayments] = useState(true);
  const [hasPendingACHPayments, setHasPendingACHPayments] =
    useState(achDefaultValue);
  const [orderFetchCompleted, setOrderFetchCompleted] = useState(false);
  const [error, setError] = useState(false);
  const REACT_APP_ACH_PAYMENT_LIMIT =
    process.env.REACT_APP_ACH_PAYMENT_LIMIT || 15000;
  const REACT_APP_CARD_PAYMENT_LIMIT =
    process.env.REACT_APP_CARD_PAYMENT_LIMIT || 8000;
  const [availableAchLimit, setAvailableAchLimit] = useState(
    process.env.REACT_APP_ACH_PAYMENT_LIMIT || 15000
  );
  const [availableCardLimit, setAvailableCardLimit] = useState(
    process.env.REACT_APP_CARD_PAYMENT_LIMIT || 8000
  );

  const classes = useStyles();

  // validation of inputField
  const handleMakePayment = () => {
    if (selectedAmount.amount < 1 && selectedAmount.amount !== 0) {
      setPaymentAmountError(l(content.amountTooLess, language));
      return;
    }
    if (
      paymentType === 'cc' &&
      isGreaterWithTolerance(selectedAmount.amount, availableCardLimit)
    ) {
      setPaymentAmountError(
        l(content.amountLimit, language) +
          ' ' +
          formatAmountToCurrencyString(
            availableCardLimit,
            selectedAmount.currency,
            language
          )
      );
      return;
    }
    setPaymentAmountError('');
  };

  const formatPlaceHolderText = language => {
    if (language === 'en-ca') {
      return `CAD$0`;
    } else if (language === 'fr-ca') {
      return `0 CAD`;
    }
    return 'Enter payment amount';
  };

  const isBalanceZero = balance.amount === 0;

  const [hasValidOrderReceipt, setHasValidOrderReceipt] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const formattedPrice = formatPriceWithCents(balance.amount, balance.currency);
  const [displayProcessingModal, setDisplayProcessingModal] = useState(false);

  useEffect(() => {
    if (!hasValidOrderReceipt) {
      isReceiptAvailable(orderId).then(result => {
        if (result) {
          setHasValidOrderReceipt(true);
        }
      });
    }
    if (orderId) {
      useOrderDetails(
        orderId,
        setTotal,
        setBalance,
        setPayments,
        setSfOrder,
        setHasPendingPayments,
        setHasPendingACHPayments,
        setOrderFetchCompleted,
        setError,
        setAvailableAchLimit,
        setAvailableCardLimit,
        orderFetchCompleted
      );
    }
  }, [orderId]);

  useEffect(() => {
    if (orderFetchCompleted) {
      console.log(`Updated available card limit: $${availableCardLimit}`);
      console.log(`Updated available ach limit: $${availableAchLimit}`);
    }
  }, [orderFetchCompleted]);

  useEffect(() => {
    handleMakePayment();
  }, [selectedAmount]);

  useEffect(() => {
    if (isBalanceZero) setDisplay(PAYMENT_COMPLETE_INFO);
    if (error) setDisplay(null);
  }, []);

  const title = isBalanceZero
    ? l(content.complete, language)
    : l(content.makePayment, language);
  const bodyContent = isBalanceZero
    ? hasPendingPayments
      ? l(content.partialSet, language)
      : l(content.fullSet, language)
    : l(content.due, language);

  const topDescription = (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h4" className={classes.title}>
          {title}
        </Typography>
      </Grid>
      <Grid item xs={12} md={10}>
        <Typography variant="body2">{bodyContent}</Typography>
      </Grid>
    </Grid>
  );

  const errorContent = (
    <Box p={3} className={classes.errorBox}>
      <Typography variant="body2" className={classes.errorText}>
        {l(content.contact, language)}
        <Link
          style={{ fontSize: 'inherit', color: 'inherit' }}
          onClick={() => navigate('/contact')}
        >
          {l(content.contact2, language)}
        </Link>
        &nbsp;
      </Typography>
    </Box>
  );

  const isDisabled =
    !paymentType ||
    (paymentType === 'cc' &&
      orderFetchCompleted &&
      (selectedAmount.amount < 1 ||
        isGreaterWithTolerance(selectedAmount.amount, availableCardLimit)));

  const bottomContent = () => {
    switch (display) {
      case CHOOSE_PAYMENT_OPTION:
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h5" className={classes.paySecurelyLabel}>
                {l(content.paySecOnline, language)}
              </Typography>
            </Grid>
            {language == 'en' && (
              <Grid item xs={12} className={classes.paymentOption}>
                <Typography variant="h6" className={classes.paymentTypeLabel}>
                  {l(content.selectPaymentType, language)}
                </Typography>
                <RadioGroup
                  aria-label="paymentType"
                  name="paymentType"
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: '10px',
                  }}
                  value={paymentType}
                  onChange={event => {
                    const newPaymentType = event.target.value;
                    if (newPaymentType == 'cc') {
                      setPaymentAmountError('');
                      setSelectedAmount({ ...selectedAmount, amount: 0 });
                    }
                    setPaymentType(newPaymentType);
                  }}
                >
                  <div>
                    <FormControlLabel
                      value="cc"
                      control={<Radio checkedIcon={<CheckedIcon />} />}
                      label={
                        <span className={classes.paymentOptionLabel}>
                          {l(content.creditCard, language)}
                        </span>
                      }
                    />

                    {paymentType === 'cc' && (
                      <>
                        <Typography
                          variant="h6"
                          className={classes.paymentOptionDescription}
                        >
                          {`${formatAmountToCurrencyString(
                            availableCardLimit,
                            selectedAmount.currency,
                            language
                          )} ${l(content.creditCardDescription, language)}`}
                        </Typography>
                        <Grid
                          item
                          xs={12}
                          className={classes.creditCardContainer}
                        >
                          <Typography
                            variant="h6"
                            className={classes.paymentAmountLabel}
                          >
                            {l(content.paymentAmount, language)}
                          </Typography>

                          <NumberFormat
                            format={value =>
                              amountToCurrencyString(
                                Number(value),
                                balance.currency,
                                language
                              ).toString()
                            }
                            placeholder={formatPlaceHolderText(prefixLanguage)}
                            customInput={props => (
                              <TextField
                                {...props}
                                autoFocus
                                helperText={paymentAmountError}
                                error={!!paymentAmountError}
                                aria-describedby="component-error-text"
                                FormHelperTextProps={{
                                  classes: {
                                    root: classes.formHelperText,
                                  },
                                }}
                                className={classes.textField}
                                inputProps={{
                                  ...props.inputProps,
                                  'aria-label': 'enter partial amount',
                                  onKeyDown: e => {
                                    if (
                                      e.key === 'ArrowLeft' ||
                                      e.key === 'ArrowRight' ||
                                      e.key === 'Home' ||
                                      e.key === 'End'
                                    ) {
                                      e.preventDefault();
                                    }
                                  },
                                  onClick: e => {
                                    const length = e.target.value.length;
                                    e.target.setSelectionRange(length, length);
                                  },
                                  onSelect: e => {
                                    const length = e.target.value.length;
                                    if (e.target.selectionStart !== length) {
                                      e.target.setSelectionRange(
                                        length,
                                        length
                                      );
                                    }
                                  },
                                }}
                              />
                            )}
                            onValueChange={values => {
                              setSelectedAmount({
                                amount: values.floatValue / 100,
                                currency: balance.currency,
                              });
                              setPaymentAmountError('');
                            }}
                            className={classes.partialAmount}
                            data-partial-payment-amount
                          />

                          <Typography
                            variant="h6"
                            className={classes.paymentTotal}
                          >
                            Payment total:{' '}
                            <b style={{ fontWeight: 'bold' }}>
                              {amountToCurrencyString(
                                (selectedAmount?.amount || 0) * 103,
                                selectedAmount?.currency || 'USD',
                                language
                              )}
                            </b>{' '}
                            (Including 3% convenience fee in the amount of{' '}
                            {amountToCurrencyString(
                              (selectedAmount?.amount || 0) * 3,
                              selectedAmount?.currency || 'USD',
                              language
                            )}
                            )
                          </Typography>
                        </Grid>
                      </>
                    )}
                  </div>
                  <div>
                    <FormControlLabel
                      value="ach"
                      control={<Radio checkedIcon={<CheckedIcon />} />}
                      label={
                        <>
                          <span className={classes.paymentOptionLabel}>
                            {l(content.ach, language)}
                          </span>
                        </>
                      }
                    />
                  </div>
                </RadioGroup>
              </Grid>
            )}

            <ChoosePaymentOption
              onPayWithBankTransferClick={() =>
                setDisplay(PAY_VIA_BANK_TRANSFER)
              }
              onPayWithCreditCardClick={() => setDisplay(PAY_VIA_CREDIT_CARD)}
              orderId={orderId}
              availableReservationLimit={availableAchLimit}
              selectedAmount={selectedAmount}
            />
            <Button
              data-cy="pay-button"
              style={{ marginTop: '20px' }}
              disabled={isDisabled}
              onClick={() => {
                if (paymentType === 'cc' && language === 'en') {
                  setDisplay(PAY_VIA_CREDIT_CARD);
                } else {
                  setDisplay(PAY_VIA_BANK_TRANSFER);
                }
              }}
              text={
                language === 'en-ca' || language === 'fr-ca'
                  ? l(content.payAch, language)
                  : l(content.continue, language)
              }
              disabledText={l(content.continue, language)}
              hoverColor="#555354"
              data-pay
              className={
                isDisabled ? classes.disabledButton : classes.activeButton
              }
            />
          </Grid>
        );
      case PAY_VIA_BANK_TRANSFER:
        return (
          <BankTransferPayment
            amountDue={balance}
            onBackToPaymentOptionsClick={() =>
              setDisplay(CHOOSE_PAYMENT_OPTION)
            }
            orderId={orderId}
            basepath={basepath}
            isOauth={isOauth}
            availableReservationLimit={availableAchLimit}
            orderFetchCompleted={orderFetchCompleted}
            hasPendingACHPayments={hasPendingACHPayments}
          />
        );
      case PAY_VIA_CREDIT_CARD:
        return (
          <CreditCardPayment
            amountDue={balance}
            onBackToPaymentOptionsClick={() => {
              setDisplay(CHOOSE_PAYMENT_OPTION);
              setSelectedAmount({ ...selectedAmount, amount: 0 });
            }}
            orderId={orderId}
            basepath={basepath}
            user={customer}
            availableReservationLimit={availableCardLimit}
            selectedAmount={selectedAmount}
          />
        );
      case PAYMENT_COMPLETE_INFO:
        return (
          <PaymentCompleteInfo info={l(content.completeContact, language)} />
        );
      default:
        return errorContent;
    }
  };

  return (
    <>
      <OrderDetailsProvider
        value={{ error, total, balance, payments, sfOrder }}
      >
        <PaymentPage
          location={location}
          topDescription={topDescription}
          bottomContent={bottomContent()}
          isLoading={!orderFetchCompleted}
          orderId={orderId}
          hasValidOrderReceipt={hasValidOrderReceipt}
        />
      </OrderDetailsProvider>

      <PayModal
        onClose={() => setIsPlaidDialogOpen(false)}
        open={isPlaidDialogOpen}
      />
    </>
  );
};

export default Payments;
